/* eslint-disable max-lines */
import { NEW_ALARM_TOAST_ID } from '@hakimo-ui/hakimo/feature-alarms';
import {
  GetUnifiedAlarmGroupPayload,
  UMAllocationQueueState,
  UnifiedAlarm,
  UnifiedAlarmGroup,
  UnifiedMonitoringAlarmCountMessage,
  UnifiedMonitoringAlarmGroupsMessage,
  UnifiedMonitoringAlarmTypeQueue,
  UnifiedMonitoringCount,
  UnifiedMonitoringMessageType,
} from '@hakimo-ui/hakimo/types';
import {
  LoadingIndicator,
  UnifiedMonitoringIcon,
} from '@hakimo-ui/hakimo/ui-elements';
import { PageLayout } from '@hakimo-ui/hakimo/ui-layout';
import {
  buildDoorToCameraMap,
  dismissToast,
  foldDoorAlarmsOntoCameras,
  toast,
  trackEvent,
  useUnifiedMonitoringWs,
  useUser,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnifiedMonitoringWsMessageHandler } from './hooks/useUnifiedMonitoringWsMessageHandler';
import { EmergencyAlarms } from './EmergencyAlarms';
import PendingAlarmPanel from './PendingAlarmPanel';
import {
  buildAlarmGroupParams,
  hasAlarmGroupParams,
  parseAlarmGroupPayloads,
} from './url-params';
import { UnifiedMonitoringAlarms } from './UnifiedMonitoringAlarms';

const UnifiedMonitoringContainer = () => {
  const [alarmGroupIdToDetailsMap, setAlarmGroupIdToDetailsMap] = useState<
    Record<string, UnifiedAlarmGroup>
  >({});
  const [isAlarmFetching, setIsAlarmFetching] = useState(false);
  // Set when the operator clicks Get alarm; cleared once the outcome is tracked.
  const getAlarmClickedAtRef = useRef<number | null>(null);
  const [pendingAlarmCount, setPendingAlarmCount] =
    useState<UnifiedMonitoringCount | null>(null);
  // Sent only to operators whose tenants have alarm type queues.
  const [alarmTypeQueues, setAlarmTypeQueues] = useState<
    UnifiedMonitoringAlarmTypeQueue[]
  >([]);
  // const [currentInvestigatedAlarmGroupId, setCurrentInvestigatedAlarmGroupId] =
  //   useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();

  const { send } = useUnifiedMonitoringWs();

  const handleAlarmCountResponse = (
    data: UnifiedMonitoringAlarmCountMessage
  ) => {
    setPendingAlarmCount({
      highPriority: data.pending_high_priority_alarm_count,
      lowPriority: data.pending_low_priority_alarm_count,
    });
    setAlarmTypeQueues(data.alarm_type_queues ?? []);
  };
  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.ALARM_COUNT,
    handleAlarmCountResponse
  );

  const fetchAlarms = useCallback(() => {
    setIsAlarmFetching(true);
    getAlarmClickedAtRef.current = Date.now();
    dismissToast(NEW_ALARM_TOAST_ID);
    send?.({
      event_type: UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUPS,
      pending_high_priority_alarm_count: pendingAlarmCount?.highPriority ?? 0,
      pending_low_priority_alarm_count: pendingAlarmCount?.lowPriority ?? 0,
    });
  }, [send, pendingAlarmCount]);

  // Emergency Alarms: pull from one alarm type's queue.
  const fetchAlarmTypeAlarm = useCallback(
    (alarmTypeId: string) => {
      setIsAlarmFetching(true);
      getAlarmClickedAtRef.current = Date.now();
      dismissToast(NEW_ALARM_TOAST_ID);
      send?.({
        event_type: UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUPS,
        alarm_type_id: alarmTypeId,
      });
    },
    [send]
  );

  const fetchAlarmsByIds = useCallback(
    (getAlarmsPayload: GetUnifiedAlarmGroupPayload[]) => {
      send?.({
        event_type: UnifiedMonitoringMessageType.GET_UNIFIED_ALARM_GROUPS,
        get_alarms_payload: getAlarmsPayload,
      });
    },
    [send]
  );

  useEffect(() => {
    if (Object.keys(alarmGroupIdToDetailsMap).length > 0) {
      return;
    }
    const getAlarmsPayload = parseAlarmGroupPayloads(searchParams);
    if (getAlarmsPayload.length > 0) {
      fetchAlarmsByIds(getAlarmsPayload);
    }
  }, [searchParams, alarmGroupIdToDetailsMap, fetchAlarmsByIds]);

  const sendAllocationEvent = (alarmGroup: UnifiedAlarmGroup) => {
    const firstAlarm = alarmGroup.alarms[0];
    if (!firstAlarm) {
      return;
    }
    const cameraGroupId = firstAlarm.cameraGroupId;
    send?.({
      event_type: UnifiedMonitoringMessageType.ALARM_ALLOCATION,
      alarm_group_id: alarmGroup.id,
      camera_group_id: cameraGroupId,
      tenant_id: alarmGroup.tenantId,
      product: alarmGroup.product,
      queue_state: UMAllocationQueueState.ASSIGNED,
      operator_id: user.email,
      unification_enabled: alarmGroup.unificationEnabled ? true : undefined,
      partition_key: alarmGroup.unificationEnabled
        ? alarmGroup.partition_key
        : undefined,
    });
  };

  const updateUrlSearchParams = (newAlarmGroups: UnifiedAlarmGroup[]) => {
    setSearchParams(buildAlarmGroupParams(searchParams, newAlarmGroups));
  };

  const totalAlarmCount = useMemo(() => {
    return Object.keys(alarmGroupIdToDetailsMap).length;
  }, [alarmGroupIdToDetailsMap]);

  const isAlarmLoadingFromUrl = useMemo(
    () => totalAlarmCount === 0 && hasAlarmGroupParams(searchParams),
    [totalAlarmCount, searchParams]
  );

  // One get_unified_monitoring_alarm event per Get alarm click, logged when the
  // response arrives: pickup_step is 'assigned' or 'no_alarm'. Get alarm stays
  // disabled until a response arrives, so the response belongs to this click
  // however long it took; slow pickups show up in latency_ms.
  const trackGetAlarmOutcome = (
    pickupStep: 'assigned' | 'no_alarm',
    alarmGroups: UnifiedAlarmGroup[] = []
  ) => {
    const clickedAt = getAlarmClickedAtRef.current;
    if (clickedAt === null) {
      return;
    }
    getAlarmClickedAtRef.current = null;
    trackEvent<unknown>('get_unified_monitoring_alarm', {
      pickup_step: pickupStep,
      latency_ms: Date.now() - clickedAt,
      alarm_group_count: alarmGroups.length,
      alarm_group_ids: alarmGroups.map((alarmGroup) => alarmGroup.id),
      products: [
        ...new Set(alarmGroups.map((alarmGroup) => alarmGroup.product)),
      ],
    });
  };

  const handleUnifiedAlarmGroupsResponse = (
    data: UnifiedMonitoringAlarmGroupsMessage
  ) => {
    if (isAlarmLoadingFromUrl && data.payload.length === 0) {
      setSearchParams(new URLSearchParams());
      if (data.comments) {
        toast(data.comments, {
          type: 'error',
        });
      }
      return;
    }
    if (data.payload.length === 0) {
      trackGetAlarmOutcome('no_alarm');
      toast('Alarm got picked up by another operator, please try again', {
        type: 'info',
      });
      setIsAlarmFetching(false);
      return;
    }
    const newAlarmGroupIdToDetailsMap = data.payload.reduce(
      (acc, alarmGroup) => {
        const updatedAlarmGroup = { ...alarmGroup };
        const uniqueAlarmIds = new Set<string>();
        const camToAlarmsMap = new Map<string, UnifiedAlarm[]>();
        // Enterprise alarms arrive raised on the door; fold the door-only copies
        // onto their camera so a camera is one entry here, not two.
        const alarms = foldDoorAlarmsOntoCameras(
          alarmGroup.alarms,
          buildDoorToCameraMap(alarmGroup.alarms)
        );
        updatedAlarmGroup.alarms = alarms;
        alarms.forEach((alarm) => {
          if (uniqueAlarmIds.has(alarm.id)) {
            return;
          }
          uniqueAlarmIds.add(alarm.id);
          if (camToAlarmsMap.has(alarm.cameraId)) {
            camToAlarmsMap.get(alarm.cameraId)?.push(alarm);
          } else {
            camToAlarmsMap.set(alarm.cameraId, [alarm]);
          }
        });
        updatedAlarmGroup.uniqueAlarmIds = uniqueAlarmIds;
        updatedAlarmGroup.camToAlarmsMap = camToAlarmsMap;

        acc[alarmGroup.id] = updatedAlarmGroup;

        return acc;
      },
      {} as Record<string, UnifiedAlarmGroup>
    );
    setAlarmGroupIdToDetailsMap(newAlarmGroupIdToDetailsMap);
    setIsAlarmFetching(false);
    trackGetAlarmOutcome('assigned', data.payload);

    if (!isAlarmLoadingFromUrl) {
      for (const alarmGroup of Object.values(newAlarmGroupIdToDetailsMap)) {
        sendAllocationEvent(alarmGroup);
      }
    }

    updateUrlSearchParams(Object.values(newAlarmGroupIdToDetailsMap));
  };

  useUnifiedMonitoringWsMessageHandler(
    UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUPS,
    handleUnifiedAlarmGroupsResponse
  );

  const navigateToPendingAlarms = () => {
    setAlarmGroupIdToDetailsMap({});
    setSearchParams(new URLSearchParams());
  };

  // const firstLocation = useMemo(() => {
  //   if (
  //     currentInvestigatedAlarmGroupId &&
  //     alarmGroupIdToDetailsMap[currentInvestigatedAlarmGroupId]
  //   ) {
  //     return alarmGroupIdToDetailsMap[currentInvestigatedAlarmGroupId].location;
  //   }
  //   const firstAlarmGroup = Object.values(alarmGroupIdToDetailsMap)[0];
  //   return firstAlarmGroup?.location;
  // }, [alarmGroupIdToDetailsMap, currentInvestigatedAlarmGroupId]);

  // const isGridView = useMemo(() => {
  //   return totalAlarmCount > 1 && !currentInvestigatedAlarmGroupId;
  // }, [totalAlarmCount, currentInvestigatedAlarmGroupId]);

  // const pageActions = useMemo(() => {
  //   if (!firstLocation) return undefined;
  //   return [
  //     <PageHeaderActions
  //       key="page-actions"
  //       locationId={firstLocation.id.toString()}
  //       isGridView={isGridView}
  //     />,
  //   ];
  // }, [firstLocation, isGridView]);

  return (
    <PageLayout
      title="Unified Monitoring"
      // titleFontSize="large"
      // pageActions={pageActions}
    >
      <PageLayout.Content>
        {(totalAlarmCount === 0 || isAlarmLoadingFromUrl) && (
          // Header in get alarm screen
          <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 flex-none px-4 py-3">
            <div className="flex items-center space-x-2">
              <UnifiedMonitoringIcon className="dark:text-dark-text h-6 w-6 text-gray-800" />
              <span className="dark:text-dark-text text-lg font-semibold text-gray-900">
                Unified Monitoring
              </span>
            </div>
          </div>
        )}
        {isAlarmLoadingFromUrl && (
          <div className="flex h-full w-full items-center justify-center">
            <LoadingIndicator text="Loading alarms..." />
          </div>
        )}
        {!isAlarmLoadingFromUrl && (
          <PendingAlarmPanel
            aside={
              <EmergencyAlarms
                queues={alarmTypeQueues}
                fetchAlarmTypeAlarm={fetchAlarmTypeAlarm}
                isLoading={isAlarmFetching}
              />
            }
            fetchAlarms={fetchAlarms}
            isLoading={isAlarmFetching}
            shownAlarmsCount={totalAlarmCount}
          />
        )}
        {totalAlarmCount > 0 && !isAlarmLoadingFromUrl && (
          <UnifiedMonitoringAlarms
            alarmGroupIdToDetailsMap={alarmGroupIdToDetailsMap}
            totalAlarmCount={totalAlarmCount}
            navigateToPendingAlarms={navigateToPendingAlarms}
            // onInvestigateAlarmChange={setCurrentInvestigatedAlarmGroupId}
          />
        )}
      </PageLayout.Content>
    </PageLayout>
  );
};

export default withErrorBoundary(UnifiedMonitoringContainer);
